import React from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Heading,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { formatAddress } from "../helpers";
import {
  ADMIN_CREATE_ORGANIZATION_ONE_TIME_REQUEST,
  ADMIN_ORGANIZATION_REQUEST_CONTEXT,
  LIST_ORGANIZATION_CLIENTS_FOR_REQUEST,
} from "../graphql";
import {
  AdminOrganizationRequestContext,
  Category,
  FacilityContactMode,
  FacilityMode,
  NewFacilityContactInput,
  NewFacilityInput,
  OrganizationClientProfileData,
  OrganizationFacility,
} from "../types";
import {
  hasOrganizationRequestCreateValidationErrors,
  OrganizationRequestCreateValidationErrors,
  validateOrganizationRequestCreate,
} from "../validation";
import { RequestDetailsSection } from "./RequestDetailsSection";

type RelayResponse<T> = {
  pageInfo: {
    hasNextPage: boolean;
    endCursor?: string | null;
  };
  edges: Array<{
    cursor: string;
    node: T;
  }>;
};

type OrganizationRequestFormProps = {
  categories: Category[];
};

const blankFacility: NewFacilityInput = {
  name: "",
  address: {
    address: "",
    formattedAddress: "",
    city: "",
    state: "",
  },
};

const blankFacilityContact: NewFacilityContactInput = {
  name: "",
  phoneNumber: "",
  email: "",
};

const toDateTimeInput = (date: string) => `${date}T00:00:00.000Z`;

const displayOrganizationName = (profile?: OrganizationClientProfileData | null) =>
  profile?.organization?.name || profile?.billingName || "Unnamed organization";

const buildFacilityInput = (facility: NewFacilityInput) => ({
  name: facility.name.trim(),
  address: {
    address: facility.address.address.trim(),
    city: facility.address.city?.trim(),
    state: facility.address.state?.trim(),
  },
});

const buildFacilityContactInput = (contact: NewFacilityContactInput) => ({
  name: contact.name.trim(),
  phoneNumber: contact.phoneNumber.trim(),
  ...(contact.email.trim() ? { email: contact.email.trim() } : {}),
});

export const OrganizationRequestForm = ({ categories }: OrganizationRequestFormProps) => {
  const history = useHistory();
  const toast = useToast();
  const [search, setSearch] = React.useState("");
  const [selectedOrganization, setSelectedOrganization] =
    React.useState<OrganizationClientProfileData | null>(null);
  const [facilities, setFacilities] = React.useState<OrganizationFacility[]>([]);
  const [facilityMode, setFacilityMode] = React.useState<FacilityMode>("existing");
  const [selectedFacilityId, setSelectedFacilityId] = React.useState("");
  const [newFacility, setNewFacility] = React.useState<NewFacilityInput>(blankFacility);
  const [facilityContactMode, setFacilityContactMode] =
    React.useState<FacilityContactMode>("existing");
  const [selectedFacilityContactId, setSelectedFacilityContactId] = React.useState("");
  const [newFacilityContact, setNewFacilityContact] =
    React.useState<NewFacilityContactInput>(blankFacilityContact);
  const [categoryId, setCategoryId] = React.useState("");
  const [otherCategoryText, setOtherCategoryText] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [startDate, setStartDate] = React.useState("");
  const [submitAttempted, setSubmitAttempted] = React.useState(false);

  const [searchOrganizations, searchOrganizationsResult] = useLazyQuery<{
    organizationClientProfiles: RelayResponse<OrganizationClientProfileData>;
  }>(LIST_ORGANIZATION_CLIENTS_FOR_REQUEST, {
    fetchPolicy: "network-only",
  });

  const [loadOrganizationContext, organizationContextResult] = useLazyQuery<{
    adminOrganizationRequestContext: AdminOrganizationRequestContext;
  }>(ADMIN_ORGANIZATION_REQUEST_CONTEXT, {
    fetchPolicy: "network-only",
    onCompleted: ({ adminOrganizationRequestContext }) => {
      setSelectedOrganization(adminOrganizationRequestContext.organizationClientProfile);
      setFacilities(adminOrganizationRequestContext.facilities || []);
      const firstFacility = adminOrganizationRequestContext.facilities?.[0];
      setSelectedFacilityId(firstFacility?.id || "");
      setFacilityMode(firstFacility ? "existing" : "new");
      const firstContact = firstFacility?.contacts?.[0];
      setSelectedFacilityContactId(firstContact?.id || "");
      setFacilityContactMode(firstContact ? "existing" : "new");
    },
  });

  const [createOrganizationRequest, createOrganizationRequestResult] = useMutation(
    ADMIN_CREATE_ORGANIZATION_ONE_TIME_REQUEST,
    {
      onCompleted: (data) => {
        const orderCode = data?.adminCreateOrganizationOneTimeRequest?.order?.orderCode;
        history.push(orderCode ? `/orders/${orderCode}` : "/orders/all");
      },
      onError: (error) => {
        toast({
          title: "Request could not be created",
          description: error.message,
          status: "error",
        });
      },
    }
  );

  const organizations =
    searchOrganizationsResult.data?.organizationClientProfiles.edges.map((edge) => edge.node) ||
    [];
  const visibleOrganizations = search.trim().length >= 2 ? organizations : [];
  const selectedFacility = facilities.find((facility) => facility.id === selectedFacilityId);
  const contacts = selectedFacility?.contacts || [];

  const validationErrors = React.useMemo(
    () =>
      validateOrganizationRequestCreate({
        clientProfileId: selectedOrganization?.clientProfile?.id,
        facilityMode,
        selectedFacilityId,
        newFacility,
        facilityContactMode,
        selectedFacilityContactId,
        newFacilityContact,
        categoryId,
        otherCategoryText,
        startDate,
      }),
    [
      categoryId,
      facilityContactMode,
      facilityMode,
      newFacility,
      newFacilityContact,
      otherCategoryText,
      selectedFacilityContactId,
      selectedFacilityId,
      selectedOrganization?.clientProfile?.id,
      startDate,
    ]
  );
  const visibleErrors: OrganizationRequestCreateValidationErrors = submitAttempted
    ? validationErrors
    : {};

  React.useEffect(() => {
    const searchTerm = search.trim();

    if (selectedOrganization || searchTerm.length < 2) {
      return undefined;
    }

    const debounceId = window.setTimeout(() => {
      searchOrganizations({
        variables: {
          first: 10,
          after: null,
          search: searchTerm,
        },
      });
    }, 350);

    return () => window.clearTimeout(debounceId);
  }, [search, searchOrganizations, selectedOrganization]);

  React.useEffect(() => {
    if (facilityMode === "new") {
      setFacilityContactMode("new");
      setSelectedFacilityContactId("");
    }
  }, [facilityMode]);

  React.useEffect(() => {
    if (facilityMode === "existing") {
      const nextContact = contacts[0];
      setSelectedFacilityContactId(nextContact?.id || "");
      setFacilityContactMode(nextContact ? "existing" : "new");
    }
  }, [contacts, facilityMode, selectedFacilityId]);

  const handleSelectOrganization = (organization: OrganizationClientProfileData) => {
    if (!organization.clientProfile?.id) {
      toast({
        title: "Organization is missing a client profile",
        status: "error",
      });
      return;
    }

    loadOrganizationContext({
      variables: {
        clientProfileId: organization.clientProfile.id,
      },
    });
  };

  const handleChangeOrganization = () => {
    setSelectedOrganization(null);
    setFacilities([]);
    setSelectedFacilityId("");
    setSelectedFacilityContactId("");
    setFacilityMode("existing");
    setFacilityContactMode("existing");
    setSearch("");
  };

  const handleSubmit = () => {
    setSubmitAttempted(true);

    if (hasOrganizationRequestCreateValidationErrors(validationErrors)) {
      toast({
        title: "Review the highlighted fields",
        status: "warning",
      });
      return;
    }

    if (!selectedOrganization?.clientProfile?.id) {
      return;
    }

    createOrganizationRequest({
      variables: {
        data: {
          clientProfileId: selectedOrganization.clientProfile.id,
          ...(facilityMode === "existing"
            ? { facilityId: selectedFacilityId }
            : { facility: buildFacilityInput(newFacility) }),
          ...(facilityContactMode === "existing"
            ? { facilityContactId: selectedFacilityContactId }
            : { facilityContact: buildFacilityContactInput(newFacilityContact) }),
          ...(otherCategoryText.trim()
            ? { otherCategoryText: otherCategoryText.trim() }
            : { categoryId }),
          ...(description.trim() ? { description: description.trim() } : {}),
          startDate: toDateTimeInput(startDate),
        },
      },
    });
  };

  return (
    <Stack spacing={5}>
      {selectedOrganization ? (
        <Box bg="white" borderWidth="1px" borderColor="gray.200" rounded="md" p={4}>
          <HStack justify="space-between" align="start" mb={3}>
            <Box>
              <Heading size="sm">{displayOrganizationName(selectedOrganization)}</Heading>
              <Text color="gray.600" fontSize="sm">
                Billing: {selectedOrganization.billingName || "Not set"}
              </Text>
            </Box>
            <Stack align="flex-end" spacing={2}>
              <Badge colorScheme="purple">Organization</Badge>
              <Button size="xs" variant="outline" onClick={handleChangeOrganization}>
                Change
              </Button>
            </Stack>
          </HStack>
          <Stack spacing={2} fontSize="sm" color="gray.700">
            <Text>
              Primary contact: {selectedOrganization.contactName || "Not set"} ·{" "}
              {selectedOrganization.contactEmail || "No email"} ·{" "}
              {selectedOrganization.contactPhoneNumber || "No phone"}
            </Text>
            <Text>Profile: {selectedOrganization.clientProfile?.id || "N/A"}</Text>
            <Text>Billing address: {selectedOrganization.billingAddress ? formatAddress(selectedOrganization.billingAddress) : "N/A"}</Text>
          </Stack>
        </Box>
      ) : (
        <Box bg="white" borderWidth="1px" borderColor="gray.200" rounded="md" p={5}>
          <Heading size="sm" mb={4}>
            Organization
          </Heading>
          <FormControl isInvalid={Boolean(visibleErrors.organization)}>
            <FormLabel htmlFor="organization-search">Search organizations</FormLabel>
            <Input
              id="organization-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Organization, billing name, contact, email, or phone"
            />
            <FormErrorMessage>{visibleErrors.organization}</FormErrorMessage>
          </FormControl>
          {searchOrganizationsResult.loading && (
            <Text color="gray.500" fontSize="sm" mt={3}>
              Searching organizations...
            </Text>
          )}
          {visibleOrganizations.length > 0 && (
            <Stack spacing={3} mt={4}>
              {visibleOrganizations.map((organization) => (
                <Button
                  key={organization.id}
                  variant="outline"
                  justifyContent="flex-start"
                  h="auto"
                  py={3}
                  whiteSpace="normal"
                  onClick={() => handleSelectOrganization(organization)}
                >
                  <Box textAlign="left">
                    <Text fontWeight="semibold">
                      {displayOrganizationName(organization)}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {organization.billingName || "No billing name"} ·{" "}
                      {organization.contactName || "No primary contact"}
                    </Text>
                  </Box>
                </Button>
              ))}
            </Stack>
          )}
        </Box>
      )}

      <Box bg="white" borderWidth="1px" borderColor="gray.200" rounded="md" p={5}>
        <Heading size="sm" mb={4}>
          Facility
        </Heading>
        {!selectedOrganization ? (
          <Text color="gray.500">Select an organization before choosing a facility.</Text>
        ) : organizationContextResult.loading ? (
          <Text color="gray.500">Loading organization facilities...</Text>
        ) : (
          <Stack spacing={4}>
            <FormControl isInvalid={Boolean(visibleErrors.facility)}>
              <RadioGroup
                aria-label="Facility"
                value={facilityMode === "existing" ? selectedFacilityId : "new"}
                onChange={(value) => {
                  if (value === "new") {
                    setFacilityMode("new");
                    return;
                  }

                  setFacilityMode("existing");
                  setSelectedFacilityId(value);
                }}
              >
                <Stack spacing={3}>
                  {facilities.map((facility) => (
                    <Radio key={facility.id} value={facility.id}>
                      {facility.name} · {facility.address ? formatAddress(facility.address) : "No address"}
                    </Radio>
                  ))}
                  <Radio value="new">Add a new facility</Radio>
                </Stack>
              </RadioGroup>
              <FormErrorMessage>{visibleErrors.facility}</FormErrorMessage>
            </FormControl>
            {!facilities.length && (
              <Alert status="info" rounded="md">
                <AlertIcon />
                This organization has no saved facilities yet.
              </Alert>
            )}
            {facilityMode === "new" && (
              <Stack spacing={3}>
                <Divider />
                <FormControl isRequired isInvalid={Boolean(visibleErrors.newFacility?.name)}>
                  <FormLabel htmlFor="new-facility-name">Facility name</FormLabel>
                  <Input
                    id="new-facility-name"
                    value={newFacility.name}
                    onChange={(event) =>
                      setNewFacility((current) => ({
                        ...current,
                        name: event.target.value,
                      }))
                    }
                  />
                  <FormErrorMessage>{visibleErrors.newFacility?.name}</FormErrorMessage>
                </FormControl>
                <FormControl
                  isRequired
                  isInvalid={Boolean(visibleErrors.newFacility?.address?.address)}
                >
                  <FormLabel htmlFor="new-facility-address">Address line</FormLabel>
                  <Input
                    id="new-facility-address"
                    value={newFacility.address.address}
                    onChange={(event) =>
                      setNewFacility((current) => ({
                        ...current,
                        address: {
                          ...current.address,
                          address: event.target.value,
                          formattedAddress:
                            current.address.formattedAddress || event.target.value,
                        },
                      }))
                    }
                  />
                  <FormErrorMessage>
                    {visibleErrors.newFacility?.address?.address}
                  </FormErrorMessage>
                </FormControl>
                <FormControl
                  isRequired
                  isInvalid={Boolean(visibleErrors.newFacility?.address?.formattedAddress)}
                >
                  <FormLabel htmlFor="new-facility-formatted-address">
                    Formatted address
                  </FormLabel>
                  <Input
                    id="new-facility-formatted-address"
                    value={newFacility.address.formattedAddress}
                    onChange={(event) =>
                      setNewFacility((current) => ({
                        ...current,
                        address: {
                          ...current.address,
                          formattedAddress: event.target.value,
                        },
                      }))
                    }
                  />
                  <FormErrorMessage>
                    {visibleErrors.newFacility?.address?.formattedAddress}
                  </FormErrorMessage>
                </FormControl>
                <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={3}>
                  <FormControl
                    isRequired
                    isInvalid={Boolean(visibleErrors.newFacility?.address?.city)}
                  >
                    <FormLabel htmlFor="new-facility-city">City</FormLabel>
                    <Input
                      id="new-facility-city"
                      value={newFacility.address.city}
                      onChange={(event) =>
                        setNewFacility((current) => ({
                          ...current,
                          address: { ...current.address, city: event.target.value },
                        }))
                      }
                    />
                    <FormErrorMessage>
                      {visibleErrors.newFacility?.address?.city}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl
                    isRequired
                    isInvalid={Boolean(visibleErrors.newFacility?.address?.state)}
                  >
                    <FormLabel htmlFor="new-facility-state">State</FormLabel>
                    <Input
                      id="new-facility-state"
                      value={newFacility.address.state}
                      onChange={(event) =>
                        setNewFacility((current) => ({
                          ...current,
                          address: { ...current.address, state: event.target.value },
                        }))
                      }
                    />
                    <FormErrorMessage>
                      {visibleErrors.newFacility?.address?.state}
                    </FormErrorMessage>
                  </FormControl>
                </Grid>
              </Stack>
            )}
          </Stack>
        )}
      </Box>

      <Box bg="white" borderWidth="1px" borderColor="gray.200" rounded="md" p={5}>
        <Heading size="sm" mb={4}>
          Facility contact
        </Heading>
        {!selectedOrganization ? (
          <Text color="gray.500">Select an organization before choosing a contact.</Text>
        ) : facilityMode === "existing" && !selectedFacilityId ? (
          <Text color="gray.500">Choose a facility before choosing a contact.</Text>
        ) : (
          <Stack spacing={4}>
            {facilityMode === "existing" && (
              <FormControl isInvalid={Boolean(visibleErrors.facilityContact)}>
                <RadioGroup
                  aria-label="Facility contact"
                  value={
                    facilityContactMode === "existing" ? selectedFacilityContactId : "new"
                  }
                  onChange={(value) => {
                    if (value === "new") {
                      setFacilityContactMode("new");
                      return;
                    }

                    setFacilityContactMode("existing");
                    setSelectedFacilityContactId(value);
                  }}
                >
                  <Stack spacing={3}>
                    {contacts.map((contact) => (
                      <Radio key={contact.id} value={contact.id}>
                        {contact.name} · {contact.phoneNumber}
                        {contact.email ? ` · ${contact.email}` : ""}
                      </Radio>
                    ))}
                    <Radio value="new">Add a new facility contact</Radio>
                  </Stack>
                </RadioGroup>
                <FormErrorMessage>{visibleErrors.facilityContact}</FormErrorMessage>
              </FormControl>
            )}
            {!contacts.length && facilityMode === "existing" && (
              <Alert status="info" rounded="md">
                <AlertIcon />
                This facility has no saved contacts yet.
              </Alert>
            )}
            {(facilityMode === "new" || facilityContactMode === "new") && (
              <Stack spacing={3}>
                {facilityMode === "existing" && <Divider />}
                <FormControl
                  isRequired
                  isInvalid={Boolean(visibleErrors.newFacilityContact?.name)}
                >
                  <FormLabel htmlFor="new-facility-contact-name">Contact name</FormLabel>
                  <Input
                    id="new-facility-contact-name"
                    value={newFacilityContact.name}
                    onChange={(event) =>
                      setNewFacilityContact((current) => ({
                        ...current,
                        name: event.target.value,
                      }))
                    }
                  />
                  <FormErrorMessage>
                    {visibleErrors.newFacilityContact?.name}
                  </FormErrorMessage>
                </FormControl>
                <FormControl
                  isRequired
                  isInvalid={Boolean(visibleErrors.newFacilityContact?.phoneNumber)}
                >
                  <FormLabel htmlFor="new-facility-contact-phone">Phone number</FormLabel>
                  <Input
                    id="new-facility-contact-phone"
                    value={newFacilityContact.phoneNumber}
                    onChange={(event) =>
                      setNewFacilityContact((current) => ({
                        ...current,
                        phoneNumber: event.target.value,
                      }))
                    }
                  />
                  <FormErrorMessage>
                    {visibleErrors.newFacilityContact?.phoneNumber}
                  </FormErrorMessage>
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="new-facility-contact-email">Email</FormLabel>
                  <Input
                    id="new-facility-contact-email"
                    value={newFacilityContact.email}
                    onChange={(event) =>
                      setNewFacilityContact((current) => ({
                        ...current,
                        email: event.target.value,
                      }))
                    }
                  />
                </FormControl>
              </Stack>
            )}
          </Stack>
        )}
      </Box>

      <RequestDetailsSection
        categories={categories}
        categoryId={categoryId}
        otherCategoryText={otherCategoryText}
        description={description}
        startDate={startDate}
        isSubmitting={createOrganizationRequestResult.loading}
        errors={{
          category: visibleErrors.category,
          startDate: visibleErrors.startDate,
        }}
        onCategoryChange={(value) => {
          setCategoryId(value);
          setOtherCategoryText("");
        }}
        onOtherCategoryTextChange={(value) => {
          setOtherCategoryText(value);
          setCategoryId("");
        }}
        onDescriptionChange={setDescription}
        onStartDateChange={setStartDate}
        onSubmit={handleSubmit}
      />
    </Stack>
  );
};
